import { Expand } from "@jewlr/storybook/animate"
import { Box, Button, Flex, Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { Controller } from "react-hook-form"
import styled from "styled-components"

import FloatInput from "components/ui/float-input"
import Modal from "components/ui/modal"
import Radio from "components/ui/radio"
import withGiftCard from "hoc/with-gift-card"
import {
  GiftCardSelect,
  GiftCardDarkTextArea,
  GiftCardDate,
  GiftCardFieldWrapper,
} from "info/gift_cards/index"

const GiftCardFieldBox = styled(GiftCardFieldWrapper)`
  border: 1px solid ${(props) => props.theme.colors.grey10};
  text-align: left;
`
const GiftCardDateBox = styled(Box)`
  label {
    font-size: 10px;
  }
`

const CheckoutButton = styled(Button).attrs((props) => ({
  fontSize: props.theme.infoGiftCards.checkoutButton.fontSize,
  fontWeight: props.theme.infoGiftCards.checkoutButton.fontWeight,
}))`
  color: white;
  height: 43px;
  margin: auto;
  text-transform: ${(props) => props.theme.infoGiftCards.textTransform};
  width: 80%;
  ${({ theme }) => theme.mediaQueries.tablet`
  width: 50%;
  `}
`
const FieldWrapper = styled(Box).attrs((props) => ({
  mb: "16px",
  ...props,
}))`
  text-align: left;
`

const GiftCardModal = ({ amount, onClose, show }) => (
  <Modal
    label={`${sl.config.site_title} Gift Cards`}
    onClose={onClose}
    show={show}
    width="700px"
  >
    <GiftCard amount={amount} />
  </Modal>
)

GiftCardModal.propTypes = {
  amount: PropTypes.string,
  onClose: PropTypes.func,
  show: PropTypes.bool,
}

const InternalGiftCard = ({
  availableTimes,
  availableTimezones,
  control,
  errors,
  isSubmitting,
  setValue,
  watch,
}) => {
  return (
    <Box data-cy="info-giftcard-modal" margin="auto" width="85%">
      <Typography fontSize="18px" mb={1} mt={3}>
        Gift Card Amount ({sl.config.currency_code}):
      </Typography>
      <FieldWrapper mb="28px">
        <Controller
          control={control}
          name="gc.original_amount"
          render={({ field, fieldState: { error } }) => (
            <FloatInput
              {...field}
              error={error}
              id="gc.original_amount"
              label="Edit Amount"
              onChange={(e) => {
                let value = e.target.value
                let preffix = sl.config.default_currency
                let index = value.indexOf(sl.config.default_currency)

                if (index == 0) {
                  preffix = ""
                }
                field.onChange(`${preffix}${value}`)
              }}
              placeholder={`Gift Card Amount (${sl.config.currency_code})`}
            />
          )}
        />
      </FieldWrapper>
      <Typography fontSize="18px" mb={1}>
        From:
      </Typography>
      <Flex flexWrap="wrap" gutter={1}>
        <FieldWrapper
          flexBasis={{ _: "100%", tablet: "50%" }}
          mb={{ _: "16px", tablet: "28px" }}
        >
          <Controller
            control={control}
            name="gc.purchaser_name"
            render={({ field, fieldState: { error } }) => (
              <FloatInput
                {...field}
                data-cy="info-giftcard-your-name-field"
                error={error}
                id="gc.purchaser_name"
                label="Your Name"
                placeholder="Your Name"
              />
            )}
          />
        </FieldWrapper>
        <FieldWrapper flexBasis={{ _: "100%", tablet: "50%" }} mb="28px">
          <Controller
            control={control}
            name="gc.purchaser_email"
            render={({ field, fieldState: { error } }) => (
              <FloatInput
                {...field}
                data-cy="info-giftcard-your-email-field"
                error={error}
                id="gc.purchaser_email"
                label="Your Email"
                placeholder="Your Email"
                type="email"
              />
            )}
          />
        </FieldWrapper>
      </Flex>
      <Typography fontSize="18px" mb={1}>
        To:
      </Typography>
      <Flex flexWrap="wrap" gutter={1}>
        <FieldWrapper flexBasis={{ _: "100%", tablet: "50%" }}>
          <Controller
            control={control}
            name="gc.recipient_name"
            render={({ field, fieldState: { error } }) => (
              <FloatInput
                {...field}
                data-cy="info-giftcard-recipient-name-field"
                error={error}
                id="gc.recipient_name"
                label="Recipient Name"
                placeholder="Recipient's Name"
              />
            )}
          />
        </FieldWrapper>
        <FieldWrapper flexBasis={{ _: "100%", tablet: "50%" }}>
          <Controller
            control={control}
            name="gc.recipient_email"
            render={({ field, fieldState: { error } }) => (
              <FloatInput
                {...field}
                data-cy="info-giftcard-recipient-email-field"
                error={error}
                id="gc.recipient_email"
                label="Recipient Email"
                placeholder="Recipient's Email"
                type="email"
              />
            )}
          />
        </FieldWrapper>
      </Flex>
      <FieldWrapper mb={2}>
        <Controller
          control={control}
          name="gc.confirm_email"
          render={({ field, fieldState: { error } }) => (
            <FloatInput
              {...field}
              data-cy="info-giftcard-confirm-recipient-name-field"
              error={error}
              id="gc.confirm_email"
              label="Confirm Recipient's Email"
              placeholder="Confirm Recipient's Email"
              type="email"
            />
          )}
        />
      </FieldWrapper>
      <Box data-cy="info-giftcard-message-field" mb={2}>
        <GiftCardDarkTextArea
          control={control}
          name="gc.gift_message"
          placeholder="Enter a message (Optional)"
          type="textbox"
        />
      </Box>
      <div aria-label="Choose when to send your gift card" role="radiogroup">
        {[
          { label: "Send Now", value: "no" },
          { label: "Send at a Later Time", value: "yes" },
        ].map((option) => (
          <Radio
            checked={watch("show_timezone_form") === option.value}
            key={option.value}
            labelClassName="flex mb-8"
            name="show_timezone_form"
            onChange={() => setValue("show_timezone_form", option.value)}
          >
            {option.label}
          </Radio>
        ))}
      </div>
      {watch("show_timezone_form") === "yes" && (
        <Expand aria-live="polite" show>
          <Typography fontSize="18px" mb={2} mt={3}>
            Choose a Delivery Time (Optional)
          </Typography>
          <Box mb={2} textAlign="left">
            <Typography mb={1}>Send On a Date:</Typography>
            <GiftCardDateBox>
              <GiftCardDate control={control} />
            </GiftCardDateBox>
          </Box>
          <Box mb={2} textAlign="left">
            <Typography mb={1}>Select a Time:</Typography>
            <GiftCardFieldBox pr="8px">
              <GiftCardSelect
                control={control}
                id="time_gift_card"
                name="email_send_time"
                options={availableTimes}
              />
            </GiftCardFieldBox>
          </Box>
          <Typography mb={1} textAlign="left">
            Select Recipient&apos;s Timezone:
          </Typography>
          <Box mb={2}>
            <GiftCardFieldBox pr="8px">
              <GiftCardSelect
                control={control}
                id="timezone_gift_card"
                name="email_send_timezone"
                options={availableTimezones}
              />
            </GiftCardFieldBox>
          </Box>
        </Expand>
      )}
      {errors?.root?.error && (
        <Box mt={{ _: 3, tablet: 2 }}>
          <Typography color="error">{errors?.root?.error}</Typography>
        </Box>
      )}
      <Box mb={5} mt={{ _: 4, tablet: 3 }} textAlign="center">
        <CheckoutButton
          data-cy="info-giftcard-checkout-btn"
          disabled={isSubmitting}
          loading={isSubmitting}
          type="submit"
          variant="primary"
        >
          gift card checkout
        </CheckoutButton>
      </Box>
    </Box>
  )
}

InternalGiftCard.propTypes = {
  availableTimes: PropTypes.array,
  availableTimezones: PropTypes.array,
  control: PropTypes.object,
  errors: PropTypes.object,
  isSubmitting: PropTypes.bool,
  setValue: PropTypes.func,
  watch: PropTypes.func,
}

const GiftCard = withGiftCard(InternalGiftCard)

export default GiftCardModal
